import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import { useDataRefresh } from '../../context/DataRefreshContext';
import BannerFormModal from '../../components/admin/BannerFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import './Admin.css';

interface Banner {
  id: number;
  title_bn: string;
  title_en: string;
  subtitle_bn: string;
  subtitle_en: string;
  image: string;
  link: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

const BannerManagement: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { triggerRefresh } = useDataRefresh();
  const [toast, setToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'title_en', headerName: 'Title (EN)', sortable: true, filter: true, flex: 1 },
    { field: 'title_bn', headerName: 'Title (BN)', sortable: true, filter: true, flex: 1 },
    {
      field: 'image',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params: any) => (
        params.value ? <img src={params.value} alt="Banner" style={{ height: '30px', borderRadius: '4px' }} /> : 'No Image'
      )
    },
    { field: 'display_order', headerName: 'Order', width: 100, sortable: true },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      cellRenderer: (params: any) => (
        <span className={`status-badge ${params.value ? 'approved' : 'pending'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Banner"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Banner"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getBanners();
      const data = response.data || response;
      const bannersList = Array.isArray(data) ? data : (data.results || data.data || []);
      setBanners(bannersList);
    } catch (error) {
      console.error('Failed to load banners:', error);
      setToast({ show: true, message: 'Failed to load banners', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner: Banner) => {
    setSelectedBanner(banner);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (banner: Banner) => {
    setSelectedBanner(banner);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;

    try {
      await adminAPI.deleteBanner(selectedBanner.id);
      setToast({ show: true, message: 'Banner deleted successfully', type: 'success' });
      loadBanners();
      // ✅ Trigger refresh for banner data
      triggerRefresh('banners');
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete banner', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedBanner(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      // Create FormData object for file upload
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, formData[key]);
        }
      });

      if (selectedBanner) {
        await adminAPI.updateBanner(selectedBanner.id, data);
        setToast({ show: true, message: 'Banner updated successfully', type: 'success' });
      } else {
        await adminAPI.createBanner(data);
        setToast({ show: true, message: 'Banner created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedBanner(null);
      loadBanners();
      // ✅ Trigger refresh for banner data
      triggerRefresh('banners');
    } catch (error) {
      console.error('Save error:', error);
      setToast({ show: true, message: 'Failed to save banner', type: 'error' });
    }
  };

  const handleAddNew = () => {
    setSelectedBanner(null);
    setIsModalOpen(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure banners is always an array before filtering
  const bannersArray = Array.isArray(banners) ? banners : [];
  const filteredBanners = bannersArray.filter(banner =>
    banner.title_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    banner.title_bn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Banner Management</h3>
          <p>Manage promotional banners and hero images</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add New Banner
          </button>
          <button className="btn btn-secondary" onClick={loadBanners}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search banners by title..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredBanners.length}</span> banners found
        </div>

        <div className="ag-grid-container">
          <AgGridReact

            theme={themeQuartz}
            rowData={filteredBanners || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}

            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      <BannerFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBanner(null);
        }}
        onSave={handleSave}
        banner={selectedBanner}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedBanner(null);
        }}
        onConfirm={handleDelete}
        title="Delete Banner"
        message={`Are you sure you want to delete "${selectedBanner?.title_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default BannerManagement;
